/*
 * Public-only supplemental filter index.
 *
 * API:
 *   buildRequestURL(sourceURL, context, {search, origin}) -> URL
 *   load({sourceURLs, context, scopeHandles, search, signal, timeoutMs, fetch, origin})
 *     -> Promise<{filters, products, sourceTotals}>
 *   count(products, filters, params) -> {total, filters}
 *   select(products, filters, params) -> products
 *
 * `load` is deliberately uncached. It rejects instead of returning partial data,
 * so callers must keep native choices in a loading/error state on failure.
 */
(function (root, factory) {
  var api = factory(root || {});
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.MerlonsFilterIndex = api;
}(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this), function (root) {
  'use strict';

  var GROUPS = ['size', 'color', 'material', 'surface', 'lighting', 'power', 'brand', 'ip', 'bulb_type', 'wattage', 'mounting_type'];
  var PARAM_PREFIX = 'filter.p.m.merlons_catalog.filter_';
  var DEFAULT_TIMEOUT_MS = 25000;
  var MAX_CONCURRENCY = 2;

  function paramName(key) { return PARAM_PREFIX + key + '_reference'; }
  function isReference(name) { return GROUPS.some(function (key) { return name === paramName(key); }); }
  function hasReferences(params) { return GROUPS.some(function (key) { return params.getAll(paramName(key)).some(Boolean); }); }
  function abortError(message) {
    var error = new Error(message || 'Filter index request aborted');
    error.name = 'AbortError';
    return error;
  }
  function asParams(value) {
    if (value instanceof URLSearchParams) return new URLSearchParams(value.toString());
    if (typeof value === 'string') return new URLSearchParams(value.charAt(0) === '?' ? value.slice(1) : value);
    if (value && typeof value.toString === 'function') return new URLSearchParams(value.toString());
    return new URLSearchParams();
  }
  function valuesFor(params, name) {
    var values = [];
    params.getAll(name).forEach(function (raw) {
      String(raw).split(',').forEach(function (value) { if (value) values.push(value); });
    });
    return Array.from(new Set(values));
  }
  function expectedOrigin(options, source) {
    if (options && options.origin) return options.origin;
    if (root.location && root.location.origin) return root.location.origin;
    return source.origin;
  }
  function buildRequestURL(sourceURL, context, options) {
    options = options || {};
    var base = root.location && root.location.href ? root.location.href : 'https://merlons.invalid/';
    var url = new URL(sourceURL, base);
    var origin = expectedOrigin(options, url);
    if (url.origin !== origin) throw new TypeError('Filter index source must be same-origin');

    var contextParams = asParams(context);
    contextParams.forEach(function (_value, name) { url.searchParams.delete(name); });
    contextParams.forEach(function (value, name) { url.searchParams.append(name, value); });

    // Native index reads are unfiltered by default. A caller that already has
    // canonical reference selections may explicitly retain them for an index
    // read; all other query cleanup remains unchanged.
    if (!options.preserveReferences) {
      GROUPS.forEach(function (key) { url.searchParams.delete(paramName(key)); });
    }
    ['page', 'view', 'section_id', 'q', 'options', 'optionsprefix', 'options[prefix]', 'type', 'merlons_sort', 'merlons_offer'].forEach(function (name) {
      url.searchParams.delete(name);
    });
    if (options.search) {
      var query = contextParams.get('q');
      if (query) url.searchParams.set('q', query);
      var prefix = contextParams.get('options[prefix]');
      url.searchParams.set('options[prefix]', prefix || 'last');
      url.searchParams.set('type', 'product');
    }
    url.searchParams.set('view', 'merlons-filter-index');
    return url;
  }
  function decodeLabel(value) {
    var text = String(value == null ? '' : value);
    if (typeof document !== 'undefined' && document.createElement) {
      var node = document.createElement('textarea');
      node.innerHTML = text;
      return node.value;
    }
    return text.replace(/&#(x[0-9a-f]+|\d+);/gi, function (_match, code) {
      var number = code.charAt(0).toLowerCase() === 'x' ? parseInt(code.slice(1), 16) : parseInt(code, 10);
      return Number.isFinite(number) ? String.fromCodePoint(number) : _match;
    }).replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&apos;/g, "'").replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  }
  function validEntry(entry) {
    return entry && typeof entry.id === 'string' && entry.id.indexOf('gid://shopify/Metaobject/') === 0 && typeof entry.label === 'string';
  }
  function validatePayload(payload, expectedPage) {
    if (!payload || payload.version !== 1 || payload.page !== expectedPage || !Number.isInteger(payload.pages) || payload.pages < 1 || payload.page > payload.pages || !Number.isInteger(payload.total) || payload.total < 0 || !Array.isArray(payload.filters) || !Array.isArray(payload.products)) {
      throw new TypeError('Malformed filter index response');
    }
    var filterKeys = new Set();
    payload.filters.forEach(function (filter) {
      if (!filter || GROUPS.indexOf(filter.key) === -1 || filter.paramName !== paramName(filter.key) || typeof filter.label !== 'string' || (filter.operator !== 'AND' && filter.operator !== 'OR') || filterKeys.has(filter.key)) throw new TypeError('Malformed filter index filter metadata');
      filterKeys.add(filter.key);
    });
    if (filterKeys.size !== GROUPS.length) throw new TypeError('Incomplete filter index filter metadata');
    payload.products.forEach(function (product) {
      if (!product || (typeof product.id !== 'string' && typeof product.id !== 'number') || !product.values || !Array.isArray(product.collections) || !Array.isArray(product.offers) || typeof product.available !== 'boolean' || !Number.isInteger(product.priceMin) || !Number.isInteger(product.priceMax)) throw new TypeError('Malformed filter index product');
      GROUPS.forEach(function (key) {
        if (!Array.isArray(product.values[key]) || !product.values[key].every(validEntry)) throw new TypeError('Malformed filter index reference value');
      });
      if (!product.collections.every(function (handle) { return typeof handle === 'string'; }) || !product.offers.every(function (offer) { return offer === 'discount' || offer === 'liquidation'; })) throw new TypeError('Malformed filter index product metadata');
    });
    return payload;
  }
  function fetchPage(url, page, fetchFn, signal) {
    var pageURL = new URL(url.toString());
    if (page > 1) pageURL.searchParams.set('page', String(page));
    else pageURL.searchParams.delete('page');
    return fetchFn(pageURL.toString(), { cache: 'no-store', signal: signal }).then(function (response) {
      if (!response || !response.ok) throw new Error('Filter index request failed');
      return response.json();
    }).then(function (payload) { return validatePayload(payload, page); });
  }
  function mapLimit(items, limit, iteratee) {
    var next = 0;
    var output = new Array(items.length);
    function worker() {
      if (next >= items.length) return Promise.resolve();
      var index = next++;
      return Promise.resolve(iteratee(items[index], index)).then(function (result) {
        output[index] = result;
        return worker();
      });
    }
    return Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker)).then(function () { return output; });
  }
  function load(options) {
    options = options || {};
    var sources = Array.from(new Set((options.sourceURLs || []).map(String)));
    if (!sources.length) return Promise.reject(new TypeError('Filter index needs at least one source URL'));
    var fetchFn = options.fetch || root.fetch;
    if (typeof fetchFn !== 'function') return Promise.reject(new TypeError('Filter index needs fetch'));
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var signal = controller ? controller.signal : options.signal;
    var externalAbort = function () { if (controller) controller.abort(); };
    if (options.signal) {
      if (options.signal.aborted) return Promise.reject(abortError());
      options.signal.addEventListener('abort', externalAbort, { once: true });
    }
    var rejectTimeout;
    var didTimeout = false;
    var timeout = setTimeout(function () {
      didTimeout = true;
      if (controller) controller.abort();
      if (rejectTimeout) rejectTimeout(new Error('Filter index request timed out'));
    }, options.timeoutMs == null ? DEFAULT_TIMEOUT_MS : Math.min(DEFAULT_TIMEOUT_MS, Math.max(1, options.timeoutMs)));
    var timeoutPromise = new Promise(function (_resolve, reject) { rejectTimeout = reject; });
    var urls;
    try { urls = sources.map(function (source) { return buildRequestURL(source, options.context, options); }); }
    catch (error) { clearTimeout(timeout); if (options.signal) options.signal.removeEventListener('abort', externalAbort); return Promise.reject(error); }

    var work = mapLimit(urls, MAX_CONCURRENCY, function (url) { return fetchPage(url, 1, fetchFn, signal); }).then(function (firstPages) {
      var requests = [];
      firstPages.forEach(function (first, sourceIndex) {
        for (var page = 2; page <= first.pages; page += 1) requests.push({ sourceIndex: sourceIndex, page: page });
      });
      return mapLimit(requests, MAX_CONCURRENCY, function (request) {
        return fetchPage(urls[request.sourceIndex], request.page, fetchFn, signal).then(function (payload) { return { request: request, payload: payload }; });
      }).then(function (rest) {
        var sourcePages = firstPages.map(function (payload) { return [payload]; });
        rest.forEach(function (item) { sourcePages[item.request.sourceIndex].push(item.payload); });
        var shape = function (payload) {
          return payload.filters.map(function (filter) { return filter.key + '|' + filter.paramName + '|' + filter.operator; }).sort().join(',');
        };
        sourcePages.forEach(function (pages, sourceIndex) {
          var first = firstPages[sourceIndex];
          var numbers = new Set(pages.map(function (payload) { return payload.page; }));
          if (pages.length !== first.pages || numbers.size !== first.pages || Array.from(numbers).some(function (page) { return page < 1 || page > first.pages; }) || pages.some(function (payload) { return payload.pages !== first.pages || payload.total !== first.total || shape(payload) !== shape(first); })) {
            throw new TypeError('Incomplete or inconsistent filter index pages');
          }
          var productCount = pages.reduce(function (sum, payload) { return sum + payload.products.length; }, 0);
          if (productCount !== first.total) throw new TypeError('Incomplete filter index product set');
          var uniqueIds = new Set();
          pages.forEach(function (payload) { payload.products.forEach(function (product) { uniqueIds.add(String(product.id)); }); });
          if (uniqueIds.size !== productCount) throw new TypeError('Duplicate products within filter index source');
        });
        return { all: sourcePages.flat(), firstPages: firstPages };
      });
    }).then(function (loaded) {
      if (signal && signal.aborted) throw abortError();
      var productsById = new Map();
      loaded.all.forEach(function (payload) {
        payload.products.forEach(function (product) {
          var id = String(product.id);
          if (!productsById.has(id)) productsById.set(id, product);
        });
      });
      var scope = new Set((options.scopeHandles || []).filter(function (handle) { return typeof handle === 'string' && handle; }));
      var products = Array.from(productsById.values()).filter(function (product) {
        return !scope.size || product.collections.some(function (handle) { return scope.has(handle); });
      });
      return {
        filters: loaded.firstPages[0].filters,
        products: products,
        sourceTotals: loaded.firstPages.map(function (payload) { return payload.total; })
      };
    });
    return Promise.race([work, timeoutPromise]).catch(function (error) {
      if (didTimeout) throw new Error('Filter index request timed out');
      if ((signal && signal.aborted) || (options.signal && options.signal.aborted)) throw abortError();
      throw error;
    }).finally(function () {
      clearTimeout(timeout);
      if (options.signal) options.signal.removeEventListener('abort', externalAbort);
    });
  }
  function selectedValues(params, filter) { return new Set(valuesFor(params, filter.paramName)); }
  function offerValues(params) {
    var selected = valuesFor(params, 'merlons_offer');
    if (!selected.length) {
      var sort = params.get('merlons_sort');
      if (sort === 'discount' || sort === 'liquidation') selected = [sort];
    }
    return new Set(selected.filter(function (offer) { return offer === 'discount' || offer === 'liquidation'; }));
  }
  function groupMatches(product, filter, selected) {
    if (!selected.size) return true;
    var ids = new Set((product.values[filter.key] || []).map(function (entry) { return entry.id; }));
    if (filter.operator === 'AND') return Array.from(selected).every(function (id) { return ids.has(id); });
    return Array.from(selected).some(function (id) { return ids.has(id); });
  }
  function offerMatches(product, offers, legacy) {
    var values = legacy && Array.isArray(product.legacyOffers) ? product.legacyOffers : product.offers;
    return !offers.size || Array.from(offers).some(function (offer) { return values.indexOf(offer) !== -1; });
  }
  function matches(product, filters, selections, offers, legacy) {
    if (!offerMatches(product, offers, legacy)) return false;
    return filters.every(function (filter) { return groupMatches(product, filter, selections.get(filter.key)); });
  }
  function select(products, filters, params) {
    var source = Array.isArray(products) ? products : [];
    var normalizedFilters = (Array.isArray(filters) ? filters : []).filter(function (filter) {
      return filter && GROUPS.indexOf(filter.key) !== -1 && filter.paramName === paramName(filter.key) && (filter.operator === 'AND' || filter.operator === 'OR');
    });
    var query = asParams(params);
    var selections = new Map(normalizedFilters.map(function (filter) { return [filter.key, selectedValues(query, filter)]; }));
    var offers = offerValues(query);
    var legacy = !query.getAll('merlons_offer').length && !query.get('q') && Boolean(query.get('merlons_sort'));
    return source.filter(function (product) { return matches(product, normalizedFilters, selections, offers, legacy); });
  }
  function count(products, filters, params) {
    var source = Array.isArray(products) ? products : [];
    var normalizedFilters = (Array.isArray(filters) ? filters : []).filter(function (filter) {
      return filter && GROUPS.indexOf(filter.key) !== -1 && filter.paramName === paramName(filter.key) && (filter.operator === 'AND' || filter.operator === 'OR');
    });
    var query = asParams(params);
    var selections = new Map(normalizedFilters.map(function (filter) { return [filter.key, selectedValues(query, filter)]; }));
    var offers = offerValues(query);
    var legacy = !query.getAll('merlons_offer').length && !query.get('q') && Boolean(query.get('merlons_sort'));
    var total = source.filter(function (product) { return matches(product, normalizedFilters, selections, offers, legacy); }).length;
    var resultFilters = normalizedFilters.map(function (filter) {
      var entries = new Map();
      source.forEach(function (product) {
        (product.values && product.values[filter.key] || []).forEach(function (entry) {
          if (validEntry(entry) && !entries.has(entry.id)) entries.set(entry.id, { id: entry.id, label: decodeLabel(entry.label), number: entry.number, mode: entry.mode });
        });
      });
      var current = selections.get(filter.key);
      // Keep active bookmarked values removable even when the current source no
      // longer contains their metaobject (for example, after a catalogue edit).
      current.forEach(function (id) { if (!entries.has(id)) entries.set(id, { id: id, label: '' }); });
      var counts = new Map(Array.from(entries.keys()).map(function (id) { return [id, 0]; }));
      source.forEach(function (product) {
        if (!offerMatches(product, offers, legacy)) return;
        var otherGroupsMatch = normalizedFilters.every(function (other) {
          return other.key === filter.key || groupMatches(product, other, selections.get(other.key));
        });
        if (!otherGroupsMatch) return;
        var productIds = new Set((product.values[filter.key] || []).map(function (entry) { return entry.id; }));
        if (filter.operator === 'AND' && !Array.from(current).every(function (id) { return productIds.has(id); })) return;
        productIds.forEach(function (id) {
          if (counts.has(id)) counts.set(id, counts.get(id) + 1);
        });
      });
      var orderedEntries = Array.from(entries.values());
      if (filter.key === 'wattage') orderedEntries.sort(function (a, b) {
        var numberA = Number.isFinite(a.number) ? a.number : Number((a.label.match(/\d+(?:[.,]\d+)?/) || [Infinity])[0].toString().replace(',', '.'));
        var numberB = Number.isFinite(b.number) ? b.number : Number((b.label.match(/\d+(?:[.,]\d+)?/) || [Infinity])[0].toString().replace(',', '.'));
        return numberA - numberB || Number(a.mode === 'maximum') - Number(b.mode === 'maximum') || a.label.localeCompare(b.label);
      });
      var values = orderedEntries.map(function (entry) {
        return { id: entry.id, label: entry.label, active: current.has(entry.id), count: counts.get(entry.id) || 0 };
      });
      return { key: filter.key, paramName: filter.paramName, label: decodeLabel(filter.label), operator: filter.operator, values: values };
    });
    return { total: total, filters: resultFilters };
  }
  return { GROUPS: GROUPS.slice(), isReference: isReference, hasReferences: hasReferences, buildRequestURL: buildRequestURL, load: load, count: count, select: select, decodeLabel: decodeLabel };
}));
